
import { Component, Vue } from 'vue-property-decorator';
import { UserModule } from '@/store/user';
import { createPost, getAdminPhone } from '@/api/irPost';
import { getCompanyInfo, getCompanyName } from '@/api/company'; // getCompanyInfo 추가
import { getUserInfo } from '@/api/user'; // getUserInfo 추가
import type { IIrPostCreateRequest } from '@/types/irPost';
import moment from 'moment';

interface ICompany {
  userUid: string
  coName: string
}

@Component({
  name: 'IrPostCreate',
})
export default class extends Vue {
  private formData: IIrPostCreateRequest = {
    toUserUid: '',
    fromUserPhone: '',
    fromUserName: '', // fromUserName 필드 추가
    title: '',
    content: '',
  };

  private investorList: ICompany[] = [];

  private isSubmitting = false;

  private fromUserCompanyName = ''; // 현재 로그인 사용자 회사명

  private isUserInfoLocked = true; // 사용자 정보 필드 잠금 상태

  public get formattedFromUserPhone(): string {
    const rawNumber = this.formData.fromUserPhone.replace(/[^0-9]/g, '');
    let formattedNumber = '';

    if (rawNumber.length < 4) {
      formattedNumber = rawNumber;
    } else if (rawNumber.length < 7) {
      formattedNumber = `${rawNumber.substring(0, 3)}-${rawNumber.substring(3)}`;
    } else if (rawNumber.length < 11) {
      formattedNumber = `${rawNumber.substring(0, 3)}-${rawNumber.substring(3, 7)}-${rawNumber.substring(7)}`;
    } else {
      formattedNumber = `${rawNumber.substring(0, 3)}-${rawNumber.substring(3, 7)}-${rawNumber.substring(7, 11)}`;
    }
    return formattedNumber;
  }

  public set formattedFromUserPhone(value: string) {
    this.formData.fromUserPhone = value.replace(/[^0-9]/g, ''); // 숫자만 저장
  }

  get matchingCompanyName(): string {
    const selectedInvestor = this.investorList.find(
      (investor) => investor.userUid === this.formData.toUserUid,
    );
    return selectedInvestor ? selectedInvestor.coName : '회사 정보 없음';
  }

  // 취소 버튼 핸들러 추가
  private handleCancel(): void {
    this.$router.back();
  }

  // 제출
  private async handleSubmit(): Promise<void> {
    if (this.isSubmitting) return;

    // 유효성 검사
    if (!this.formData.toUserUid) {
      // TODO: alert 대신 더 나은 UI 알림(토스트, 모달 등) 사용을 고려하세요.
      // console.log('투자사를 선택해주세요.');
      return;
    }

    if (!this.formData.title.trim()) {
      // TODO: alert 대신 더 나은 UI 알림(토스트, 모달 등) 사용을 고려하세요.
      // console.log('제목을 입력해주세요.');
      return;
    }

    if (!this.formData.content.trim()) {
      // TODO: alert 대신 더 나은 UI 알림(토스트, 모달 등) 사용을 고려하세요.
      // console.log('내용을 입력해주세요.');
      return;
    }

    this.isSubmitting = true;

    try {
      const response = await createPost(this.formData);

      // ✅ SMS 발송 로직 추가
      try {
        const adminPhoneResponse = await getAdminPhone();
        const adminPhone = adminPhoneResponse.data.phone;
        // console.log('Admin Phone:', adminPhone);

        await this.sendSms(
          adminPhone,
          moment().format('YYYY-MM-DD'),
          this.fromUserCompanyName,
          this.matchingCompanyName,
          'A', // As requested by user
        );
      } catch (smsError) {
        console.error('❌ SMS 서비스 호출 중 오류:', smsError);
        // SMS 실패는 전체 신청 프로세스에 영향을 주지 않음
      }

      // TODO: alert 대신 더 나은 UI 알림(토스트, 모달 등) 사용을 고려하세요.
      // console.log('IR 매칭 신청이 완료되었습니다.');

      // 목록 페이지로 이동
      this.$router.push({
        name: 'Chat',
        params: {
          // menuUid: 'f3e3cdbd-b57b-40fe-ad1a-f55d55ee4a12', // 하드코딩된 UID
          menuUid: 'da5150a8-c524-4997-a73e-194447f8e686', // 하드코딩된 UID
        },
      });

      // console.log('생성된 게시글:', response.data);
    } catch (error) {
      // console.error('게시글 생성 실패:', error);
      // TODO: alert 대신 더 나은 UI 알림(토스트, 모달 등) 사용을 고려하세요.
      // console.log('IR 매칭 신청에 실패했습니다. 다시 시도해주세요.');
    } finally {
      this.isSubmitting = false;
    }
  }

  // SMS 발송 헬퍼 함수
  private async sendSms(
    recipientPhone: string,
    irDate: string,
    irCo1: string,
    irCo2: string,
    irTp: string,
  ): Promise<void> {
    const smsData = new FormData();
    smsData.append('irp', recipientPhone);
    smsData.append('irdate', irDate);
    smsData.append('ircoA', irCo1);
    smsData.append('ircoB', irCo2);
    smsData.append('irtp', irTp);

    try {
      const smsResponse = await fetch('https://www.azen.co.kr/sms_service/sms_service_gnst.php', {
        method: 'POST',
        body: smsData,
      });

      const responseText = await smsResponse.text();
      // console.log(`SMS 서버 응답 (${recipientPhone}):`, responseText);
    } catch (smsError) {
      // console.error(`❌ SMS 서비스 호출 중 오류 (${recipientPhone}):`, smsError);
    }
  }

  private async created(): Promise<void> {
    const investCompanyUid = this.$route.query.investCompanyUid as string;

    if (investCompanyUid) {
      try {
        const res = await getCompanyName(investCompanyUid);
        const companyName = res.data.coName;

        this.investorList = [{ userUid: investCompanyUid, coName: companyName }];
        this.formData.toUserUid = investCompanyUid;
      } catch (error) {
        console.error(`회사 정보 조회 실패 (${investCompanyUid}):`, error);
        this.investorList = [{ userUid: investCompanyUid, coName: `(회사명 조회 실패: ${investCompanyUid})` }];
        this.formData.toUserUid = investCompanyUid;
      }
    }

    // 현재 로그인한 사용자의 회사명 추출
    if (UserModule.token) {
      try {
        const uRes = await getUserInfo();
        const { uid } = uRes.data;
        // console.log(uRes.data);

        if (uid) {
          const ucRes = await getCompanyInfo(uid);
          this.fromUserCompanyName = ucRes.data.coName || '';
          this.formData.fromUserPhone = uRes.data.phone || ''; // 연락처 초기값 설정
          this.formData.fromUserName = uRes.data.actualName || ''; // 담당자 이름 초기값 설정
        }
      } catch (error) {
        console.error('현재 사용자 회사 정보 조회 실패:', error);
      }
    }
  }
}
